import { useCallback, useEffect, useRef, useState } from 'react';

import { notifications } from '@/components/Toast';

const DEV_MODE_STORAGE_KEY = 'pulse_dev_mode_enabled';
const CLICK_THRESHOLD = 7;
const CLICK_TIMEOUT = 3000; // 3 seconds to complete all clicks

export const useDevMode = () => {
  const [isDevMode, setIsDevMode] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DEV_MODE_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const clickCountRef = useRef(0);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Save to localStorage whenever dev mode changes
  useEffect(() => {
    try {
      localStorage.setItem(DEV_MODE_STORAGE_KEY, String(isDevMode));
    } catch (error) {
      console.error('Failed to save dev mode state:', error);
    }
  }, [isDevMode]);

  // Reset click count after timeout
  const resetClickCount = useCallback(() => {
    clickCountRef.current = 0;
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }
  }, []);

  // Handle clicks for dev mode activation
  const handleDevModeClick = useCallback(() => {
    clickCountRef.current += 1;

    // Clear existing timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }

    // If we've reached the threshold, activate dev mode
    if (clickCountRef.current >= CLICK_THRESHOLD) {
      setIsDevMode((prev) => {
        const newState = !prev;

        // Show toast notification outside setState to ensure it fires
        setTimeout(() => {
          if (newState) {
            notifications.success(
              'Dev mode activated! Press Cmd+Shift+F to open feature flags.',
              {
                duration: 5000,
                position: 'top-right',
              },
            );
          } else {
            notifications.info('Dev mode deactivated', {
              duration: 3000,
              position: 'top-right',
            });
          }
        }, 0);

        return newState;
      });
      resetClickCount();
    } else {
      // Set timeout to reset click count
      clickTimeoutRef.current = setTimeout(() => {
        resetClickCount();
      }, CLICK_TIMEOUT);
    }
  }, [resetClickCount]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, []);

  // Manually enable/disable dev mode
  const enableDevMode = useCallback(() => {
    setIsDevMode(true);
  }, []);

  const disableDevMode = useCallback(() => {
    setIsDevMode(false);
  }, []);

  const toggleDevMode = useCallback(() => {
    setIsDevMode((prev) => !prev);
  }, []);

  return {
    isDevMode,
    handleDevModeClick,
    enableDevMode,
    disableDevMode,
    toggleDevMode,
  };
};
